import React from 'react';

import styled from '@emotion/styled';

import BookmarkJob from '../../../section/dashboard/BookmarkJob';
import JobsIcon from '../../../components/Icons/JobsIcon';
import Img from '../../../components/Img/Img';
import {
  extractTextFromHTML,
  truncateText,
  truncateTextByCharacters,
} from '../../../utils/utils';
import { ModalProvider } from '../../../context/ModalContext';
import { Modal } from '../../../components/Modals/Modal';
import ModalTriggerContainer from '../../../components/Modals/ModalTriggerContainer';
import JobSummaryModal from '../../../components/Modals/ModalsActions/JobSummaryModal/JobSummaryModal';
import ModalContainer from '../../../components/Modals/ModalContainer';
import { RootState, useAppSelector } from '../../../redux/store';
import CancelBookmarkJob from '../../../section/dashboard/CancelBookmarkJob';

interface IAppCard {
  imgSrc: string;
  institutionName: string;
  jobTitle: string;
  yearsOfExperience: string;
  pay: number;
  className?: string;
  jobId: string;
  jobDescription?: string;
  location: string;
  payInterval: string;
  jobStartDate?: string;
  jobEndDate?: string;
}

const ApplicationCard: React.FC<IAppCard> = ({
  imgSrc,
  institutionName,
  jobTitle,
  yearsOfExperience,
  pay,
  className,
  jobId,
  jobDescription,
  location,
  payInterval,
  jobStartDate,
  jobEndDate,
}) => {
  const bookmarkMapping = useAppSelector(
    (state: RootState) => state.bookmarkedLocumJobs.bookmarkMapping
  );
  const isBookmarked = bookmarkMapping.some(
    (mapping) => mapping.jobId === jobId
  );

  return (
    <ModalProvider>
      <ModalTriggerContainer>
        <Container className={className}>
          <LeftContent>
            <LeftContentHeader>
              <ImageContainer>
                <Img src={imgSrc} alt="logo" />
              </ImageContainer>
              <LeftContentText>
                {' '}
                {(() => {
                  const text = extractTextFromHTML(institutionName);
                  const first20 = text.slice(0, 10);
                  const spaceCount = (first20.match(/ /g) || []).length;
                  return spaceCount === 0
                    ? truncateTextByCharacters(text, 7)
                    : truncateText(text, 2);
                })()}
              </LeftContentText>
            </LeftContentHeader>

            <LeftContentLowerPart>
              <JobCardDescription>
                {jobDescription
                  ? (() => {
                      const text = extractTextFromHTML(jobDescription);
                      const first20 = text.slice(0, 20);
                      const spaceCount = (first20.match(/ /g) || []).length;
                      return spaceCount < 4
                        ? truncateTextByCharacters(text, 30)
                        : truncateText(text, 14);
                    })()
                  : jobTitle}
              </JobCardDescription>
              <LeftContentFooter>
                <JobAgeArea>
                  <JobsIcon />
                  {yearsOfExperience} year+
                </JobAgeArea>

                <div>{pay?.formatCurrency()}</div>
              </LeftContentFooter>
            </LeftContentLowerPart>
          </LeftContent>

          <IconContainer>
            {isBookmarked ? (
              <CancelBookmarkJob jobId={jobId} />
            ) : (
              <BookmarkJob jobId={jobId} />
            )}
          </IconContainer>
        </Container>
      </ModalTriggerContainer>
      <Modal>
        <ModalContainer width="35rem" padding="1rem">
          <JobSummaryModal
            institutionName={institutionName}
            jobTitle={jobTitle}
            yearsOfExperience={yearsOfExperience}
            pay={pay}
            jobDescription={jobDescription}
            imgSrc={imgSrc}
            location={location}
            payInterval={payInterval}
            jobStartDate={jobStartDate}
            jobEndDate={jobEndDate}
          />
        </ModalContainer>
      </Modal>
    </ModalProvider>
  );
};

export default ApplicationCard;

const Container = styled.div`
  display: grid;
  grid-template-columns: auto 32px;
  gap: 0.5rem;
  align-items: flex-start;
  // border: 1px solid ${({ theme }) => theme.palette.borderColor};
  border-radius: 0.3125rem;
  padding: 1rem;
  height: 12.3rem;
  min-width: 17rem;
  width: fit-content;
  /* border: 2px solid red; */
  background-color: white;
  box-shadow: 0 4px 12px rgba(58, 60, 65, 0.03), 0 2px 4px rgba(27, 27, 27, 0.06);


  &.cursor-pointer {
    cursor: pointer;
  }

  &.active {
    border: 1px solid #2f80ed;
    background-color: #edf5ff;
  }

  @media (max-width: 884px) {
    width: 19.7rem;
  }

  @media (max-width: 480px) {
    width: 90vw;
    max-width: 25rem;
  }
`;

const ImageContainer = styled.div`
  width: 40px;
  height: 40px;
  /* border: 2px solid red; */
`;

const LeftContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  gap: 0.38rem;
`;

const JobAgeArea = styled.div`
  display: flex;
  align-items: center;
  gap: 0.2rem;

  & > svg {
    width: 12px;
    height: 12px;
  }
`;

const LeftContentHeader = styled.div`
  display: flex;
  /* border: 2px solid red; */
  align-items: center;
  gap: 0.38rem;
`;

const LeftContentFooter = styled.div`
  display: flex;
  gap: 1.5rem;
  color: ${({ theme }) => theme.palette.greyGrey1};
  font-size: 0.875rem;
  font-weight: 400;
`;

const LeftContentText = styled.div`
  font-size: 1.25rem;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.blackBlack2};
`;

const LeftContentLowerPart = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
`;

const JobCardDescription = styled.div`
  font-size: 1rem;
  font-weight: 300;
  color: ${({ theme }) => theme.palette.greyGrey1};
`;

const IconContainer = styled.div`
  margin-left: auto;
`;
